import { memo } from "react";
import { Toast, Flex, Stack, Box } from "@chakra-ui/react";
import Icon from "@ui-kit/icons/Icon.web";
import Text from "@ui-kit/typography/Text/Text.web";
import type { ToastAlertSharedProps } from "./ToastAlert.props";

export const alertPaintMapBackground: Record<string, string> = {
  success: "green.100",
  error: "red.100",
  warning: "yellow.100",
  info: "blue.100",
};

export const alertPaintMapAccent: Record<string, string> = {
  success: "green.500",
  error: "red.500",
  warning: "yellow.500",
  info: "blue.500",
};

export const alertPaintMapText: Record<string, string> = {
  success: "green.800",
  error: "red.800",
  warning: "yellow.800",
  info: "blue.800",
};

// Memoized icon component to prevent re-creation on every render
const ToastIcon = memo(function ToastIcon({ status }: { status: string }) {
  const color = alertPaintMapText[status] || alertPaintMapText.info;
  if (status === "error") {
    return <Icon.Error outline color={color} boxSize="element.xl" />;
  }
  if (status === "warning") {
    return <Icon.Warning outline color={color} boxSize="element.xl" />;
  }
  if (status === "info" || status === "loading") {
    return <Icon.Info outline color={color} boxSize="element.xl" />;
  }
  // success - checkmark in a circle
  return <Icon.TickCircle outline color={color} boxSize="element.xl" />;
});

const TOAST_WIDTH = "359px";

// Memoized to prevent infinite re-renders when used in Toaster context
const ToastAlert = memo(function ToastAlert({
  status = "success",
  title,
  description,
}: ToastAlertProps): JSX.Element {
  // Cursor prop exists in type definition but Typescript doesn't recognize it
  const CloseTrigger = Toast.CloseTrigger as React.FC<
    React.ComponentProps<typeof Toast.CloseTrigger> & {
      cursor?: string;
    }
  >;
  return (
    <Toast.Root
      width={TOAST_WIDTH}
      color={alertPaintMapText[status]}
      borderLeft="4px solid"
      borderColor={alertPaintMapAccent[status]}
      background={alertPaintMapBackground[status]}
      data-testid={`${status}-toast-alert`}
    >
      <Flex direction="row" align="flex-start" gap="md" flex="1">
        <Box flexShrink={0}>
          <ToastIcon status={status} />
        </Box>
        <Stack gap="2xs" flex="1">
          {title && (
            <Text fontWeight={700} fontSize="md">
              {title}
            </Text>
          )}
          {description && <Text fontSize="sm">{description}</Text>}
        </Stack>
      </Flex>
      <CloseTrigger aria-label="Close toast" cursor="pointer" />
    </Toast.Root>
  );
});

export default ToastAlert;

export type ToastAlertProps = ToastAlertSharedProps;
